// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.
//
// D-POT-2 — asp-pot "Security Awareness Training — Acknowledgment Required" (index.html #trainingModal,
// dashboard.html training gate, app.js acknowledgeTraining, server.js /api/training/*). Owner ruling
// 2026-10-20: the same security training and logs as POT. Wording verbatim. Shown on every POT classic
// page until the signed-in user has an acknowledgment on file (GET carerx/training/status); the
// acknowledgment is logged server-side with name, IP, user agent, app version and timestamp, and
// written to the security audit log (carerx.training.acknowledged). An admin reset re-shows it.
import { useEffect, useState } from 'react'
import { acknowledgeCareRxTraining, getCareRxTrainingStatus } from '../../../api/client'
import { useAuth } from '../../../auth/AuthContext'
import { POT } from '../potTheme'

// Keyed by account: the flag must not carry over to another login in the same browser tab.
const sessionKey = (id?: string | null) => `pot_classic_training_ack:${id ?? ''}`

export function SecurityTraining() {
  const [show, setShow] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const { me } = useAuth()
  const SESSION_KEY = sessionKey(me?.account_id)

  useEffect(() => {
    if (!me?.account_id) return
    let alive = true
    try { if (sessionStorage.getItem(SESSION_KEY) === '1') return } catch { /* no session storage — ask the server */ }
    getCareRxTrainingStatus().then(r => {
      if (!alive) return
      if (r.acknowledged) { try { sessionStorage.setItem(SESSION_KEY, '1') } catch { /* fine */ } }
      else setShow(true)
    }).catch(() => { /* status unavailable (offline / older server): do not block the form */ })
    return () => { alive = false }
  }, [me?.account_id, SESSION_KEY])

  if (!show) return null
  const ack = async () => {
    setBusy(true); setErr(null)
    try {
      await acknowledgeCareRxTraining({ user_agent: navigator.userAgent })
      try { sessionStorage.setItem(SESSION_KEY, '1') } catch { /* fine */ }
      setShow(false)
    } catch { setErr('Could not record your acknowledgment — check your connection and try again.') }
    finally { setBusy(false) }
  }
  const h4 = { fontSize: 13, fontWeight: 600 as const, color: POT.text, margin: '12px 0 4px' }
  const ul = { fontSize: 12, color: POT.text2, margin: '0 0 0 16px', padding: 0, lineHeight: 1.7 }
  return (
    <div role="dialog" aria-modal="true" aria-labelledby="pot-training-title" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 310, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: POT.font }}>
      <div style={{ background: '#fff', borderRadius: POT.radiusLg, maxWidth: 560, width: '100%', maxHeight: '92vh', overflowY: 'auto', padding: '28px 24px', boxShadow: '0 8px 40px rgba(0,0,0,.15)' }}>
        <h3 id="pot-training-title" style={{ fontSize: 16, fontWeight: 600, color: POT.blue, margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>🛡 Security Awareness Training — Acknowledgment Required</h3>
        <p style={{ fontSize: 13, color: POT.text2, lineHeight: 1.6, margin: '0 0 10px' }}><strong>As a user of the Plan of Treatment tool, you are required to read and acknowledge the following security training before proceeding.</strong></p>
        <div style={h4}>What is Protected Health Information (PHI)?</div>
        <p style={{ fontSize: 12, color: POT.text2, lineHeight: 1.6, margin: 0 }}>PHI includes any data that could identify a patient: names, dates of birth, MRNs, phone numbers, diagnoses, medications, and treatment plans.</p>
        <div style={h4}>Your Responsibilities</div>
        <ul style={ul}>
          <li>Never share your username or PIN with anyone</li>
          <li>Sign out when leaving your workstation unattended</li>
          <li>Only access patient records needed for your assigned care</li>
          <li>Do not screenshot, photograph, or copy PHI outside the tool</li>
          <li>Save generated PDFs per your facility's document retention policy</li>
          <li>Do not email PHI unless using an approved encrypted system</li>
          <li>Report any suspected security breach immediately to your supervisor</li>
        </ul>
        <div style={h4}>AI Usage Rules</div>
        <ul style={ul}>
          <li>AI is a documentation aid only — it does NOT make clinical decisions</li>
          <li>Always verify AI suggestions against authoritative clinical sources</li>
          <li>Do not type patient names, DOBs, or MRNs into the AI chat panel</li>
          <li>The system automatically protects PHI before sending data to the AI</li>
        </ul>
        <div style={h4}>Incident Reporting</div>
        <ul style={ul}>
          <li>Report unauthorized access, lost credentials, or unusual system behavior immediately</li>
          <li>Do not attempt to investigate security issues yourself</li>
          <li>Preserve evidence — do not delete logs or files related to an incident</li>
        </ul>
        <div style={{ background: POT.warnLt, border: `1px solid ${POT.warnBorder}`, borderRadius: 4, padding: '10px 12px', fontSize: 12, color: POT.text2, lineHeight: 1.6, margin: '14px 0 0' }}>
          <strong>HIPAA Compliance:</strong> Violations of these policies may result in disciplinary action and can carry federal penalties of up to $50,000 per incident. By clicking "I Acknowledge" below, you confirm you have read, understood, and agree to comply with these security requirements.
        </div>
        {err && <div style={{ color: POT.danger, fontSize: 12, marginTop: 10 }}>{err}</div>}
        <button type="button" onClick={() => { void ack() }} disabled={busy} style={{ width: '100%', marginTop: 14, padding: 12, border: 'none', borderRadius: 10, background: POT.blue, color: '#fff', fontSize: 14, fontWeight: 600, cursor: busy ? 'wait' : 'pointer', fontFamily: POT.font }}>{busy ? 'Recording…' : 'I Acknowledge — I Have Read and Agree'}</button>
        <p style={{ fontSize: 11, color: POT.text3, textAlign: 'center', margin: '10px 0 0' }}>Your acknowledgment is logged with your name, IP address, and timestamp for HIPAA compliance records.</p>
      </div>
    </div>
  )
}
